import styled from '@emotion/styled'
import {css} from '@emotion/react'

import {theme} from '../../theme'

// ─────────────────────────────────────────────
// Size styles
// ─────────────────────────────────────────────

const sizeStyles = ({size = 'md'}) => {
  switch (size) {
    case 'sm':
      return css`
        height: 32px;
        padding: 0 ${theme.space[2]};
        font-size: ${theme.fontSizes.sm};
      `

    case 'lg':
      return css`
        height: 46px;
        padding: 0 ${theme.space[4]};
        font-size: ${theme.fontSizes.lg};
      `

    case 'md':
    default:
      return css`
        height: 38px;
        padding: 0 ${theme.space[3]};
        font-size: ${theme.fontSizes.base};
      `
  }
}

// ─────────────────────────────────────────────
// Variant styles
// ─────────────────────────────────────────────

const variantStyles = ({variant = 'default', hasError}) => {
  switch (variant) {
    case 'form':
      return css`
        color: #6a7181;

        background: #ffffff;
        // background: #f7f7f7;
        border: 1px solid ${hasError ? '#d92d20' : '#E5E7EB'};
        border-radius: 10px;

        &:hover:not(:disabled) {
          border-color: #c8ccd2;
        }

        &:focus {
          border-color: ${hasError ? '#d92d20' : '#7c5cff'};
          box-shadow: 0 0 0 3px
            ${hasError ? 'rgba(217, 45, 32, 0.12)' : 'rgba(124, 92, 255, 0.15)'};
        }
      `

    case 'minimal':
      return css`
        background: transparent;
        border: none;
        border-bottom: 1px solid
          ${hasError ? '#d92d20' : theme.colors.border.primary};
        border-radius: 0;
      `

    case 'default':
    default:
      return css`
        background: ${theme.colors.input.bg};
        border: 1px solid
          ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.border};
        border-radius: ${theme.radii.xs};

        &:hover:not(:disabled) {
          border-color: ${theme.colors.input.borderHover};
        }

        &:focus {
          border-color: ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.borderFocus};

          box-shadow: 0 0 0 3px
            ${hasError
              ? theme.colors.input.errorRing
              : theme.colors.input.focusRing};
        }
      `
  }
}

// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  width: 100%;
`

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const LabelIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  color: #6a7181;

  svg {
    width: 12px;
    height: 12px;
  }
`

export const LabelEl = styled.label`
  display: block;

  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'form' ? theme.fontWeights.medium : theme.fontWeights.medium};

  color: ${({variant}) =>
    variant === 'form' ? theme.colors.text.primary : theme.colors.text.primary};
`

export const LabelReq = styled.span`
  display: block;

  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'form' ? theme.fontWeights.medium : theme.fontWeights.medium};

  color: ${theme.colors.status.danger.text};
`

export const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const SearchInput = styled.input`
  width: 100%;

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  color: ${theme.colors.input.text};

  border: 1px solid
    ${({hasError}) =>
      hasError ? theme.colors.input.borderError : theme.colors.input.border};

  outline: none;

  transition: border-color ${theme.transitions.normal},
    box-shadow ${theme.transitions.normal};

  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'text')};

  padding-left: ${({hasLeftIcon}) =>
    hasLeftIcon ? theme.space[8] : undefined};

  padding-right: ${theme.space[8]};

  ${sizeStyles}

  ${variantStyles}

  &::placeholder {
    color: ${theme.colors.input.placeholder};
  }

  &:hover:not(:disabled) {
    border-color: ${theme.colors.input.borderHover};
  }

  &:focus {
    border-color: ${({hasError}) =>
      hasError
        ? theme.colors.input.borderError
        : theme.colors.input.borderFocus};

    box-shadow: 0 0 0 3px
      ${({hasError}) =>
        hasError ? theme.colors.input.errorRing : theme.colors.input.focusRing};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${theme.colors.bg.disabled};
  }
`

export const OptionsPanel = styled.div`
  position: absolute;

  top: calc(100% + 6px);
  left: 0;
  right: 0;

  max-height: 240px;
  overflow-y: auto;

  padding: ${theme.space[1]};

  background: ${theme.colors.bg.primary};
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.md};

  z-index: ${theme.zIndex.dropdown};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.strong};
    border-radius: ${theme.radii.full};
  }
`

export const OptionItem = styled.button`
  display: block;

  width: 100%;
  text-align: left;

  padding: ${theme.space[2]} ${theme.space[3]};

  border: none;
  border-radius: ${theme.radii.md};

  background: ${({isHighlighted}) =>
    isHighlighted ? theme.colors.bg.tertiary : 'transparent'};

  color: ${theme.colors.text.primary};

  font-size: ${theme.fontSizes.sm};
  font-weight: ${({isSelected}) =>
    isSelected ? theme.fontWeights.semibold : theme.fontWeights.regular};

  cursor: pointer;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
    outline-offset: 2px;
  }
`

export const NoOptionsText = styled.div`
  padding: ${theme.space[2]} ${theme.space[3]};

  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`

export const IconSlot = styled.span`
  position: absolute;

  top: 50%;

  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  // color: ${theme.colors.text.primary};
  // color: #667085;

  pointer-events: none;

  font-size: 18px;

  ${({side}) =>
    side === 'left'
      ? css`
          left: ${theme.space[3]};
        `
      : css`
          right: ${theme.space[3]};
        `}
`

export const HelperText = styled.p`
  font-size: ${theme.fontSizes.sm};

  margin: 0;

  color: ${({isError}) =>
    isError ? theme.colors.status.danger.text : theme.colors.text.muted};
`
